<div class="p-8">
  <div class="flex justify-between items-center mb-6">
    <h1 class="text-3xl font-bold text-gray-900">Navigation</h1>
    <button (click)="save()" [disabled]="saving"
      class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300">
      {{ saving ? 'Saving...' : 'Save Navigation' }}
    </button>
  </div>

  <app-spinner *ngIf="loading"></app-spinner>

  <!-- Tabs -->
  <div *ngIf="!loading" class="border-b mb-6">
    <nav class="flex space-x-8">
      <button (click)="activeTab = 'header'"
        class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        [class.border-blue-500]="activeTab === 'header'" [class.text-blue-600]="activeTab === 'header'"
        [class.border-transparent]="activeTab !== 'header'" [class.text-gray-500]="activeTab !== 'header'">
        Header Navigation
      </button>
      <button (click)="activeTab = 'footer'"
        class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors"
        [class.border-blue-500]="activeTab === 'footer'" [class.text-blue-600]="activeTab === 'footer'"
        [class.border-transparent]="activeTab !== 'footer'" [class.text-gray-500]="activeTab !== 'footer'">
        Footer Navigation
      </button>
    </nav>
  </div>

  <!-- Header Navigation -->
  <div *ngIf="!loading && activeTab === 'header'" class="bg-white rounded-lg shadow">
    <div class="p-4 border-b flex justify-between items-center">
      <p class="text-sm text-gray-500">Drag items to reorder or nest under a parent.</p>
      <button (click)="openAddModal('header')" class="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
        + Add Item
      </button>
    </div>

    <div *ngIf="headerItems.length === 0" class="p-8 text-center text-gray-400">
      No header navigation items yet. Click "Add Item" to get started.
    </div>

    <div #headerList>
      <div *ngFor="let item of headerItems; let i = index" class="nav-item-group" [attr.data-item-id]="item.id">
        <!-- Parent item -->
        <div class="flex items-center px-4 py-3 hover:bg-gray-50 group border-b">
          <div *ngIf="!isFixedItem(item)" class="drag-handle-parent text-gray-400 hover:text-gray-600 cursor-grab mr-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
            </svg>
          </div>
          <div *ngIf="isFixedItem(item)" class="text-gray-300 mr-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-gray-900">{{ item.label }}</span>
            <span class="ml-2 text-xs text-gray-400">
              <ng-container *ngIf="item.type === 'partnerPage'">Page: {{ getPageName(item.pageId!) }}</ng-container>
              <ng-container *ngIf="item.type === 'url'">URL: {{ item.url }}</ng-container>
            </span>
            <span *ngIf="item.type === 'saltusPage'" class="ml-2 inline-block px-2 py-0.5 text-xs font-medium text-white rounded-full" style="background-color: #F0645A;">Saltus</span>
            <span *ngIf="item.children?.length" class="ml-2 text-xs text-blue-500">({{ item.children!.length }} children)</span>
          </div>
          <div *ngIf="!isFixedItem(item)" class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button *ngIf="i > 0" (click)="nestItem(i, i)" title="Nest under previous item"
              class="p-1 text-gray-400 hover:text-blue-600 rounded">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </button>
            <button (click)="addChildItem(i)" title="Add child item"
              class="p-1 text-gray-400 hover:text-green-600 rounded">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
            </button>
            <button (click)="openEditModal(item, 'header')" class="p-1 text-gray-400 hover:text-blue-600 rounded">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button (click)="removeItem(headerItems, i)" class="p-1 text-gray-400 hover:text-red-600 rounded">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Child drop zone (always present for drag targets) -->
        <div class="child-drop-zone ml-10 border-l border-gray-200"
          [class.has-children]="item.children?.length">
          <div *ngFor="let child of item.children; let ci = index"
            class="nav-child-item flex items-center px-4 py-2 hover:bg-gray-50 group" [attr.data-item-id]="child.id">
            <div class="drag-handle-child text-gray-300 hover:text-gray-500 cursor-grab mr-3">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-sm text-gray-700">{{ child.label }}</span>
              <span class="ml-2 text-xs text-gray-400">
                <ng-container *ngIf="child.type === 'partnerPage'">Page: {{ getPageName(child.pageId!) }}</ng-container>
                <ng-container *ngIf="child.type === 'url'">URL: {{ child.url }}</ng-container>
              </span>
              <span *ngIf="child.type === 'saltusPage'" class="ml-2 inline-block px-2 py-0.5 text-xs font-medium text-white rounded-full" style="background-color: #F0645A;">Saltus</span>
            </div>
            <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button (click)="unnestItem(i, ci)" title="Unnest (make top-level)"
                class="p-1 text-gray-400 hover:text-blue-600 rounded">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"></path>
                </svg>
              </button>
              <button (click)="openEditModal(child, 'header')" class="p-1 text-gray-400 hover:text-blue-600 rounded">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button (click)="removeItem(item.children!, ci)" class="p-1 text-gray-400 hover:text-red-600 rounded">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Footer Navigation -->
  <div *ngIf="!loading && activeTab === 'footer'" class="bg-white rounded-lg shadow">
    <div class="p-4 border-b flex justify-between items-center">
      <p class="text-sm text-gray-500">Drag to reorder. Footer navigation is a flat list (no nesting).</p>
      <button (click)="openAddModal('footer')" class="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
        + Add Item
      </button>
    </div>

    <div *ngIf="footerItems.length === 0" class="p-8 text-center text-gray-400">
      No footer navigation items yet. Click "Add Item" to get started.
    </div>

    <div #footerList class="divide-y">
      <div *ngFor="let item of footerItems; let i = index"
        class="flex items-center px-4 py-3 hover:bg-gray-50 group">
        <div class="drag-handle text-gray-400 hover:text-gray-600 cursor-grab mr-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"></path>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <span class="text-sm font-medium text-gray-900">{{ item.label }}</span>
          <span class="ml-2 text-xs text-gray-400">
            <ng-container *ngIf="item.type === 'partnerPage'">Page: {{ getPageName(item.pageId!) }}</ng-container>
            <ng-container *ngIf="item.type === 'url'">URL: {{ item.url }}</ng-container>
          </span>
          <span *ngIf="item.type === 'saltusPage'" class="ml-2 inline-block px-2 py-0.5 text-xs font-medium text-white rounded-full" style="background-color: #F0645A;">Saltus</span>
        </div>
        <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button (click)="openEditModal(item, 'footer')" class="p-1 text-gray-400 hover:text-blue-600 rounded">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>
          <button (click)="removeItem(footerItems, i)" class="p-1 text-gray-400 hover:text-red-600 rounded">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Add/Edit Modal -->
<div *ngIf="showModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
  <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
    <div class="px-6 py-4 border-b">
      <h3 class="text-lg font-semibold text-gray-900">{{ editingItem ? 'Edit' : 'Add' }} Navigation Item</h3>
    </div>
    <div class="p-6 space-y-4">
      <div>
        <label class="block text-sm font-medium mb-1">Type</label>
        <select [(ngModel)]="modalForm.type" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="partnerPage">Partner Page</option>
          <option value="saltusPage">Saltus Page</option>
          <option value="url">Manual URL</option>
        </select>
      </div>

      <div *ngIf="modalForm.type === 'partnerPage'">
        <label class="block text-sm font-medium mb-1">Page</label>
        <select [(ngModel)]="modalForm.pageId" (ngModelChange)="onPageSelected()"
          class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Select a page...</option>
          <option *ngFor="let page of availablePages" [value]="page._id">{{ page.name }}</option>
        </select>
      </div>

      <div *ngIf="modalForm.type === 'saltusPage'">
        <label class="block text-sm font-medium mb-1">Saltus Page</label>
        <select [(ngModel)]="modalForm.url" (ngModelChange)="onSaltusPageSelected()"
          class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Select a Saltus page...</option>
          <option *ngFor="let page of saltusPages" [value]="page.url">{{ page.title }}</option>
        </select>
      </div>

      <div *ngIf="modalForm.type === 'url'">
        <label class="block text-sm font-medium mb-1">URL</label>
        <input type="text" [(ngModel)]="modalForm.url" placeholder="/about-us"
          class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">Label</label>
        <input type="text" [(ngModel)]="modalForm.label" placeholder="Navigation label"
          class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
      </div>

      <div class="flex items-center justify-between">
        <label class="text-sm font-medium">Redirect to first child</label>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" [(ngModel)]="modalForm.redirectToChild" class="sr-only peer">
          <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
    </div>
    <div class="px-6 py-4 border-t flex justify-end space-x-2">
      <button (click)="closeModal()" class="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
      <button (click)="saveModalChild()" [disabled]="!modalForm.label"
        class="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-300">
        {{ editingItem ? 'Update' : 'Add' }}
      </button>
    </div>
  </div>
</div>
